/**
 * Wallet Auto-Reload setup card — lives inside the Marketplace settings tab.
 *
 * Three states:
 *   1. No card on file → "Save card" CTA → Stripe setup-mode Checkout
 *   2. Card on file, auto-reload OFF → toggle to turn it on + pickers
 *   3. Card on file, auto-reload ON → live config + "Charge $X now" test btn
 */
import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { CreditCard, Loader2, RefreshCw, ShieldCheck, Zap, AlertTriangle, CheckCircle2 } from "lucide-react";

const dollars = (c) => "$" + Math.round((c || 0) / 100).toLocaleString();

const THRESHOLD_OPTIONS = [
  { cents: 2500, label: "$25" },
  { cents: 5000, label: "$50" },
  { cents: 10000, label: "$100" },
  { cents: 25000, label: "$250" },
];
const TOPUP_OPTIONS = [
  { cents: 10000, label: "$100" },
  { cents: 25000, label: "$250" },
  { cents: 50000, label: "$500" },
  { cents: 100000, label: "$1,000" },
];

export default function AutoReloadCard() {
  const [state, setState] = useState({ loading: true, ar: null });
  const [saving, setSaving] = useState(false);
  const [linking, setLinking] = useState(false);
  const [testing, setTesting] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/marketplace/wallet/auto-reload");
      setState({ loading: false, ar: r.data });
    } catch (e) {
      setState({ loading: false, ar: null });
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load auto-reload settings");
    }
  };

  useEffect(() => { refresh(); }, []);

  const saveCard = async () => {
    setLinking(true);
    try {
      const { data } = await api.post("/marketplace/wallet/save-card", {
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start card setup");
        setLinking(false);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Card setup is unavailable");
      setLinking(false);
    }
  };

  const updateSettings = async (patch) => {
    setSaving(true);
    try {
      const r = await api.put("/marketplace/wallet/auto-reload", patch);
      setState((s) => ({ ...s, ar: r.data }));
      toast.success("Auto-reload updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const testCharge = async () => {
    setTesting(true);
    try {
      const r = await api.post("/marketplace/wallet/auto-reload/run");
      if (r.data?.ok) {
        toast.success(`Test charge succeeded — wallet credited ${dollars(r.data.amount_cents)}`);
        await refresh();
      } else {
        const msg = r.data?.error_message || r.data?.skipped_reason || r.data?.status || "Charge failed";
        toast.error(`Test charge failed: ${msg}`);
        await refresh();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Test charge errored");
    } finally {
      setTesting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 flex items-center gap-2 text-slate-500 text-sm" data-testid="auto-reload-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading auto-reload settings…
      </div>
    );
  }

  const ar = state.ar || {};
  const hasCard = !!ar.stripe_payment_method_id;
  const lastFailed = (ar.last_status || "").startsWith("failed:");

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid="auto-reload-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 shrink-0 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <Zap className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900">Wallet auto-reload</h3>
            <p className="text-sm text-slate-600 mt-0.5">
              Never miss a lead. We'll top up your wallet automatically when it dips below your threshold.
            </p>
          </div>
        </div>
        {hasCard && (
          <Switch
            checked={!!ar.enabled}
            disabled={saving}
            onCheckedChange={(v) => updateSettings({ enabled: !!v })}
            data-testid="auto-reload-toggle"
          />
        )}
      </div>

      {!hasCard && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:justify-between">
          <div className="text-sm text-slate-700">
            <div className="font-semibold text-slate-900 inline-flex items-center gap-1.5">
              <CreditCard className="w-4 h-4" /> Save a card to enable auto-reload
            </div>
            <div className="text-slate-500 mt-1">Stripe-secured. We never see your full card number.</div>
          </div>
          <Button
            onClick={saveCard}
            disabled={linking}
            className="bg-[#C75D33] hover:bg-[#A14828] text-white font-semibold"
            data-testid="auto-reload-save-card-btn"
          >
            {linking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening Stripe…</> : <><CreditCard className="w-4 h-4 mr-2" /> Save card</>}
          </Button>
        </div>
      )}

      {hasCard && (
        <>
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 mb-4 flex items-center justify-between" data-testid="auto-reload-card-on-file">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                <CreditCard className="w-4 h-4 text-slate-600" />
              </div>
              <div className="text-sm">
                <div className="font-semibold text-slate-900 capitalize">{ar.card_brand || "Card"} •••• {ar.card_last4 || "----"}</div>
                <div className="text-slate-500 text-xs inline-flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> Saved on file for auto-reload
                </div>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={saveCard} disabled={linking} className="text-slate-600" data-testid="auto-reload-update-card-btn">
              {linking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Update</>}
            </Button>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <PickerBlock
              label="When balance drops below"
              testidPrefix="auto-reload-threshold"
              value={ar.threshold_cents}
              options={THRESHOLD_OPTIONS}
              disabled={saving}
              onChange={(cents) => updateSettings({ threshold_cents: cents })}
            />
            <PickerBlock
              label="Auto-add this much"
              testidPrefix="auto-reload-topup"
              value={ar.topup_cents}
              options={TOPUP_OPTIONS}
              disabled={saving}
              onChange={(cents) => updateSettings({ topup_cents: cents })}
            />
          </div>

          <div className="mt-4 pt-4 border-t border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <StatusBlock ar={ar} />
            <Button
              onClick={testCharge}
              disabled={testing}
              variant="outline"
              className="border-slate-300 text-slate-700 font-semibold"
              data-testid="auto-reload-test-charge-btn"
            >
              {testing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Charging…</> : <>Charge {dollars(ar.topup_cents)} now</>}
            </Button>
          </div>

          {lastFailed && (
            <div className="mt-3 inline-flex items-start gap-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-900" data-testid="auto-reload-error-banner">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">Last charge failed — auto-reload turned off</div>
                <div className="mt-0.5">{ar.last_error_message || "Update your card and re-enable."}</div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PickerBlock({ label, value, options, disabled, onChange, testidPrefix }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">{label}</div>
      <div className="grid grid-cols-4 gap-1.5">
        {options.map((o) => {
          const active = Number(value) === o.cents;
          return (
            <button
              key={o.cents}
              type="button"
              disabled={disabled}
              onClick={() => onChange(o.cents)}
              className={`px-2.5 py-2 rounded-lg text-sm font-semibold border transition ${
                active
                  ? "bg-slate-900 border-slate-900 text-white"
                  : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
              } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
              data-testid={`${testidPrefix}-${o.cents}`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StatusBlock({ ar }) {
  if (!ar.enabled) {
    return (
      <div className="text-xs text-slate-500 inline-flex items-center gap-1.5">
        Auto-reload is <span className="font-semibold text-slate-700">off</span>. Flip the switch to enable.
      </div>
    );
  }
  if (ar.last_status === "succeeded") {
    return (
      <div className="text-xs text-emerald-700 inline-flex items-center gap-1.5">
        <CheckCircle2 className="w-3.5 h-3.5" />
        Auto-reload is <span className="font-semibold">on</span> — last topped up successfully.
      </div>
    );
  }
  return (
    <div className="text-xs text-emerald-700 inline-flex items-center gap-1.5">
      <CheckCircle2 className="w-3.5 h-3.5" />
      Auto-reload is <span className="font-semibold">on</span>. We'll charge your card when balance dips below {dollars(ar.threshold_cents)}.
    </div>
  );
}
